import type { Except, Merge } from '@festina-life/core'
import type { CalendarDate, DateValue } from '@internationalized/date'
import cx from 'classnames'
import { type FocusEvent, type ReactNode, useMemo, useState } from 'react'
import {
  DateField as AriaDateField,
  DateRangePicker,
  type DateRangePickerProps,
  I18nProvider,
  type RangeValue,
} from 'react-aria-components'
import { useIntl } from 'react-intl'

import { useTranslate } from '../../hooks'
import { clearButtonStyles } from '../internal/clearButton/clearButtonStyles'
import { selectStyles } from '../select/selectStyles'
import type { RawTextInputProps } from '../textInput/RawTextInput'
import { DateButton } from './internal/DateButton'
import { DateClearButton } from './internal/DateClearButton'
import { DateField } from './internal/DateField'
import { DateGroup } from './internal/DateGroup'
import { DatePopover } from './internal/DatePopover'
import { RangeCalendar } from './internal/RangeCalendar'
import { useDateFieldsState } from './internal/useDateFieldState'
import {
  handleDatePickerPointerDown,
  hasPartialDateSegments,
  hasShortYear,
  mapFromExternal,
  mapToExternal,
} from './internal/utils'

type Endpoint = 'start' | 'end'

type Props = Merge<
  Except<
    DateRangePickerProps<DateValue>,
    | 'minValue'
    | 'maxValue' // We call them "min" and "max" for better consistency with HTML input props
    | 'isDisabled' // We call it disabled elsewhere
    | 'isReadOnly' // Only disabled is supported
  > &
    Except<RawTextInputProps, 'type'>,
  {
    /** Value date in server format, assuming UTC+00:00. */
    value?: RangeValue<string | undefined>
    /** Callback for value date changes in server format, assuming UTC+00:00. */
    onChange?: (value: RangeValue<string | undefined>) => void
    min?: string
    max?: string
    /** Called when focus leaves the start endpoint. */
    onStartBlur?: () => void
    /** Called when focus leaves the end endpoint. */
    onEndBlur?: () => void
    /** Wraps the input inside the date picker. Supply aria-labelledby when including a visible React Aria Label. */
    renderInput?: (input: ReactNode) => ReactNode
  }
>

export const RawDateRangePicker = ({
  isInError = false,
  disabled = false,
  required,
  value: valueExternal,
  onChange: onChangeExternal,
  min,
  max,
  onStartBlur,
  onEndBlur,
  renderInput,
  ...props
}: Props) => {
  const { locale } = useIntl()
  const translate = useTranslate()
  const [calendarFocusedDate, setCalendarFocusedDate] = useState<CalendarDate>()
  const {
    fields,
    dateFieldKey,
    getLatestField,
    change,
    markPartial,
    completePartial,
    clear,
  } = useDateFieldsState<Endpoint>({
    start: valueExternal?.start,
    end: valueExternal?.end,
  })

  const startString = fields.start.value || undefined
  const endString = fields.end.value || undefined
  // React Aria drops partially typed segments whenever the value identity changes.
  const startValue = useMemo(() => mapFromExternal(startString), [startString])
  const endValue = useMemo(() => mapFromExternal(endString), [endString])
  const value = useMemo(
    () =>
      startValue && endValue
        ? {
            start: startValue,
            end: endValue,
          }
        : null,
    [startValue, endValue],
  )
  const isPartial = fields.start.isPartial || fields.end.isPartial

  const minValue = mapFromExternal(min)
  const maxValue = mapFromExternal(max)

  // A remembered calendar focus outside the current range is stale, e.g. after typing new dates.
  const isFocusWithinValue =
    !!calendarFocusedDate &&
    (!startValue || calendarFocusedDate.compare(startValue) >= 0) &&
    (!endValue || calendarFocusedDate.compare(endValue) <= 0)
  const calendarDefaultFocusedValue = isFocusWithinValue
    ? calendarFocusedDate
    : (startValue ?? endValue)

  const handleEndpointChange = (endpoint: Endpoint, date: string | undefined) =>
    onChangeExternal?.({
      start: valueExternal?.start || undefined,
      end: valueExternal?.end || undefined,
      [endpoint]: date,
    })

  const handleFieldChange = (endpoint: Endpoint, date: DateValue | null) => {
    change(endpoint, date?.toString() ?? '')
    if (!hasShortYear(date)) {
      handleEndpointChange(endpoint, date?.toString())
    }
  }

  const commitEndpoint = (endpoint: Endpoint, date: string | undefined) => {
    if (date !== (valueExternal?.[endpoint] || undefined)) {
      handleEndpointChange(endpoint, date)
    }
  }

  // Partially typed segments stay visible while the endpoint is committed as open.
  const handleEndpointBlur =
    (endpoint: Endpoint) => (event: FocusEvent<HTMLDivElement>) => {
      const hasLeftEndpoint = !event.currentTarget.contains(
        event.relatedTarget as Node | null,
      )
      if (!hasLeftEndpoint) {
        return
      }
      const field = getLatestField(endpoint)
      const hasEmptySegment = !!event.currentTarget.querySelector(
        '[role="spinbutton"]:not([aria-valuenow])',
      )
      if (hasPartialDateSegments(event.currentTarget)) {
        if (!field.isPartial) {
          markPartial(endpoint)
          if (valueExternal?.[endpoint]) {
            handleEndpointChange(endpoint, undefined)
          }
        }
      } else if (hasEmptySegment) {
        handleEndpointChange(endpoint, undefined)
      } else if (
        field.isPartial ||
        hasShortYear(mapFromExternal(field.value))
      ) {
        // Segments completed back to the held value emit no change from React Aria.
        completePartial(endpoint)
        commitEndpoint(endpoint, field.value || undefined)
      }
      if (endpoint === 'start') {
        onStartBlur?.()
      } else {
        onEndBlur?.()
      }
    }

  const handleClear = () => {
    clear()
    if (valueExternal?.start || valueExternal?.end) {
      onChangeExternal?.({ start: undefined, end: undefined })
    }
  }

  const dateInput = (
    <DateGroup
      isDisabled={disabled}
      isInError={isInError || isPartial}
      hasClearButton={!disabled}
      onPointerDownCapture={handleDatePickerPointerDown}
    >
      <AriaDateField
        key={`start-${dateFieldKey}`}
        slot="start"
        value={startValue ?? null}
        onChange={(start) => handleFieldChange('start', start)}
      >
        <div className="contents" onBlurCapture={handleEndpointBlur('start')}>
          <DateField
            isInError={isInError || fields.start.isPartial}
            isDisabled={disabled}
          />
        </div>
      </AriaDateField>
      <div className="opacity-50">–</div>
      <AriaDateField
        key={`end-${dateFieldKey}`}
        slot="end"
        value={endValue ?? null}
        onChange={(end) => handleFieldChange('end', end)}
      >
        <div className="contents" onBlurCapture={handleEndpointBlur('end')}>
          <DateField
            isInError={isInError || fields.end.isPartial}
            isDisabled={disabled}
          />
        </div>
      </AriaDateField>
      <div className={selectStyles.inputIconsWrapper}>
        {!disabled && <DateClearButton onClear={handleClear} />}
        <DateButton isDisabled={disabled} isInError={isInError || isPartial} />
      </div>
    </DateGroup>
  )

  return (
    <I18nProvider locale={`${locale}-u-ca-gregory`}>
      <DateRangePicker
        {...props}
        className={cx(clearButtonStyles.group, props.className)}
        isDisabled={disabled}
        isInvalid={isPartial || props.isInvalid}
        isRequired={required ?? props.isRequired}
        minValue={minValue}
        maxValue={maxValue}
        value={value}
        onChange={(value) => {
          // Typed endpoints are handled by the fields, which hold back short years until blur.
          if (!value || hasShortYear(value.start) || hasShortYear(value.end)) {
            return
          }
          onChangeExternal?.({
            start: mapToExternal({ value: value.start }),
            end: mapToExternal({ value: value.end }),
          })
        }}
        onPointerDownCapture={handleDatePickerPointerDown}
        aria-label={
          props['aria-label'] ??
          (props['aria-labelledby']
            ? undefined
            : translate('dateRangePicker.label'))
        }
        shouldForceLeadingZeros // Looks nicer (less jumps)
      >
        {renderInput ? renderInput(dateInput) : dateInput}
        <DatePopover>
          <RangeCalendar
            defaultFocusedValue={calendarDefaultFocusedValue}
            onFocusChange={setCalendarFocusedDate}
          />
        </DatePopover>
      </DateRangePicker>
    </I18nProvider>
  )
}
